import {useRef, useState} from "react";
import BaseDialog from "components/BaseDialog";
import BaseInput from "components/BaseInput";
import {useLocale, useMessages} from "i18n";
import {formatApiInstant} from "core/format/dateFormat";
import {GhostButton} from "account/components/AccountCards";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {WithdrawalTrigger, ReceiptDone, RecordedDeclaration} from "./SponsorBookingWithdrawal.styles";
import {Body, DetailList, DetailLabel, DetailValue, DialogActions, Failure, Field, Success,
	SuccessNote, SuccessTitle, SubmitButton, TermsText} from "account/withdrawal/WithdrawalSubmissionDialog.styles";
import {useSponsorWithdrawal} from "./useSponsorWithdrawal";
import {SPONSOR_LIMITS} from "./sponsorLimits";
import type {MyBookingRow} from "./myBookings";

type Props = {booking: MyBookingRow; contact: {name: string; email: string}; onReceived: () => void};

/** Contract-specific confirmation on the same page used to buy it. Opening is never a declaration. */
export function SponsorWithdrawal({booking, contact, onReceived}: Props) {
	const m = useMessages();
	const copy = m.sponsor.consumer;
	const {locale} = useLocale();
	const [open, setOpen] = useState(false);
	const trigger = useRef<HTMLButtonElement>(null);
	const withdrawal = useSponsorWithdrawal(booking.bookingId, contact.name, onReceived);
	const receipt = withdrawal.receipt;
	const confirmButton = useRef<HTMLButtonElement>(null);
	// Keyboard focus must stay inside the dialog for the whole send (kitecorp/transferify-ui#3079): the
	// name field and Cancel are disabled while it runs, and a disabled element drops focus to the page
	// body, where Escape reaches nothing. The confirm button stays focusable (`aria-disabled`, below), so
	// a send started with Enter in the name field, or by a click that did not focus the button, moves
	// focus there, in the same task as the commit that disabled the field.
	useIsomorphicLayoutEffect(() => {
		if (withdrawal.submitting) {
			confirmButton.current?.focus();
		}
	}, [withdrawal.submitting]);
	return <>
		<WithdrawalTrigger ref={trigger} onClick={() => setOpen(true)}>
			{receipt ? copy.receivedTitle : copy.trigger}
		</WithdrawalTrigger>
		<BaseDialog open={open} title={copy.title} onClose={() => setOpen(false)}
			disableRestoreFocus onTransitionExited={() => trigger.current?.focus()}>
			{receipt ? <Success role="status">
				<SuccessTitle>{copy.receivedTitle}</SuccessTitle>
				<SuccessNote>{copy.receiptNotice}</SuccessNote>
				<SuccessNote>{copy.refundNotice}</SuccessNote>
				<DetailList>
					<DetailLabel>{copy.bookingLabel}</DetailLabel><DetailValue>{receipt.bookingId}</DetailValue>
					<DetailLabel>{copy.emailLabel}</DetailLabel><DetailValue>{receipt.email}</DetailValue>
					<DetailLabel>{copy.receivedAt}</DetailLabel><DetailValue>{formatApiInstant(receipt.receivedAt, locale)}</DetailValue>
					<DetailLabel>{copy.refundDueBy}</DetailLabel><DetailValue>{formatApiInstant(receipt.refundDueBy, locale)}</DetailValue>
				</DetailList>
				<RecordedDeclaration>{receipt.declaration}</RecordedDeclaration>
				<ReceiptDone autoFocus onClick={() => setOpen(false)}>{m.common.done}</ReceiptDone>
			</Success> : <form onSubmit={event => {event.preventDefault(); void withdrawal.submit();}}>
				<Body>
					<DetailList>
						<DetailLabel>{copy.bookingLabel}</DetailLabel>
						<DetailValue>{booking.headline}<br/>{booking.bookingId}<br/>{booking.startsOn} / {booking.endsOn}</DetailValue>
						<DetailLabel>{copy.emailLabel}</DetailLabel><DetailValue>{contact.email}</DetailValue>
					</DetailList>
					<Field><BaseInput label={copy.nameLabel} value={withdrawal.name} required
						disabled={withdrawal.submitting} slotProps={{input: {maxLength: SPONSOR_LIMITS.sponsorName}}}
						onChange={event => withdrawal.setName(event.target.value)}/></Field>
					<TermsText>{copy.declaration}</TermsText>
					{withdrawal.error && <Failure role="alert">{copy[withdrawal.error]}</Failure>}
				</Body>
				<DialogActions>
					<GhostButton onClick={() => setOpen(false)} disabled={withdrawal.submitting}>{m.common.cancel}</GhostButton>
					{/* DISABLED only for an incomplete declaration; while sending it is `aria-disabled`, so it
					    keeps focus and a press joins the send already in flight. */}
					<SubmitButton ref={confirmButton} type="submit" disabled={!withdrawal.draftComplete}
						aria-disabled={withdrawal.submitting || undefined}>{copy.confirm}</SubmitButton>
				</DialogActions>
			</form>}
		</BaseDialog>
	</>;
}
